import React from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
} from '@mui/material';
import { UnbilledInvoiceService } from '../newInvoice/newInvoice.types';
import { formatDateDMY } from '../../../../utils/dateUtils';
import { CHECKED_ICON, UNCHECKED_ICON } from '../common/InvoiceTableCommon';

interface UnbilledServicesTableProps {
  services: UnbilledInvoiceService[];
  isLoading: boolean;
  currencySymbol: string;
  selectedIndices: Set<number>;
  onServiceToggle: (index: number) => void;
  onSelectAllServices: (services: UnbilledInvoiceService[]) => void;
}

const UnbilledServicesTable: React.FC<UnbilledServicesTableProps> = ({
  services,
  isLoading,
  currencySymbol,
  selectedIndices,
  onServiceToggle,
  onSelectAllServices,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Loading services...</Typography>
      </Box>
    );
  }

  if (services.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography color="text.secondary">No unbilled services found for this patient</Typography>
      </Box>
    );
  }

  return (
    <>
      <Card
        sx={{
          borderRadius: 3,
          p: 2,
          boxShadow: 'none',
          border: '1px solid #E4E7EC',
          backgroundColor: '#FFFFFF',
          mb: 2,
        }}
      >
        <TableContainer sx={{ maxHeight: { xs: 500, md: 'calc(100vh - 320px)' }, overflowX: 'auto' }}>
          <Table sx={{ borderCollapse: 'collapse', minWidth: 800 }}>
            <TableHead>
              <TableRow
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  background: '#F5F6F7',
                  '& .MuiTableCell-root': { borderBottom: 'none', background: '#F5F6F7' },
                  whiteSpace: 'nowrap',
                }}
              >
                <TableCell padding="checkbox" sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                  <Checkbox
                    checked={services.length > 0 && selectedIndices.size === services.length}
                    indeterminate={selectedIndices.size > 0 && selectedIndices.size < services.length}
                    onChange={() => onSelectAllServices(services)}
                    icon={UNCHECKED_ICON}
                    checkedIcon={CHECKED_ICON}
                  />
                </TableCell>
                <TableCell>Date</TableCell>
                <TableCell sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>Code</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Package</TableCell>
                <TableCell sx={{ width: 140 }}>Charges ({currencySymbol})</TableCell>
                <TableCell sx={{ width: 140 }}>Concession ({currencySymbol})</TableCell>
                <TableCell sx={{ width: 140 }}>Tax ({currencySymbol})</TableCell>
                <TableCell sx={{ width: 140, borderTopRightRadius: 12, borderBottomRightRadius: 12 }}>Net Amt. ({currencySymbol})</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((service, idx) => (
                <TableRow
                  key={idx}
                  onClick={() => onServiceToggle(idx)}
                  sx={{
                    background: '#FFFFFF',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                    },
                    '& td': {
                      borderTop: idx === 0 ? 'none' : undefined,
                      borderBottom: idx === services.length - 1 ? 'none' : undefined,
                      paddingY: 1.25,
                    },
                  }}
                >
                  <TableCell padding="checkbox" sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                    <Checkbox
                      checked={selectedIndices.has(idx)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => onServiceToggle(idx)}
                      icon={UNCHECKED_ICON}
                      checkedIcon={CHECKED_ICON}
                      disableRipple
                    />
                  </TableCell>
                  <TableCell sx={{ width: 180, whiteSpace: 'nowrap' }}>{formatDateDMY(service.date)}</TableCell>
                  <TableCell sx={{ minWidth: 100, whiteSpace: 'nowrap' }}>{service.billing_source_code}</TableCell>
                  <TableCell sx={{ width: 200 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {service.billing_source_name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Typography variant="body2" color={service.package_name ? 'text.primary' : 'text.secondary'}>
                      {service.package_name || 'No packages available'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 140 }}>{service.charges?.toFixed(2) ?? '0.00'}</TableCell>
                  <TableCell sx={{ width: 140 }}>{service.concession_amount?.toFixed(2) ?? '0.00'}</TableCell>
                  <TableCell sx={{ width: 140 }}>{service.tax_amount?.toFixed(2) ?? '0.00'}</TableCell>
                  <TableCell sx={{ width: 140, borderTopRightRadius: 12, borderBottomRightRadius: 12 }}>
                    {service.net_amount?.toFixed(2) ?? '0.00'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </>
  );
};

export default UnbilledServicesTable;
